// Copyright (c) 2022 WiseTime. All rights reserved.

import React, { useEffect, useRef, useState } from "react"
import { t } from "@lingui/macro"
import styled, { css, keyframes } from "styled-components/macro"
import SvgIcon from "./SvgIcon"

const Wrapper = styled.button<{ isCopied: boolean }>`
  position: relative;
  display: inline-block;
  vertical-align: top;
  padding: 2px;
  border-radius: 2px;
  height: 20px;
  transition: color 300ms ease;
  
  &:hover {
    color: ${({ theme }) => theme.colors.indigo};
  }
  
  ${({ isCopied }) => isCopied && css`
    color: ${({ theme }) => theme.colors.indigo};
  `}
`

const scale = keyframes`
  0% {
    transform: translate3d(100%, 0, 0) scale(0);
  }
  100% {
    transform: translate3d(100%, 0, 0) scale(1);
  }
`

const SuccessLabel = styled.span`
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.nero};
  font-size: ${({ theme }) => theme.sizeFontBase};
  line-height: normal;
  position: absolute;
  top: 1px;
  right: -2px;
  z-index: 10;
  background-color: ${({ theme }) => theme.colors.white};
  padding: 2px 4px;
  border-radius: 4px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 10%);
  will-change: auto;
  animation-name: ${scale};
  animation-duration: 200ms;
  animation-timing-function: ease;
  animation-fill-mode: both;
  transform-origin: left;
`

type Props = {
  text: string,
  hideLabelDelay?: number,
  className?: string,
}

const CopyToClipboard = ({
  text,
  hideLabelDelay = 1000,
  className,
}: Props) => {
  const [showCopiedText, setShowCopiedText] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout>()

  useEffect(() => () => timeoutRef.current && clearTimeout(timeoutRef.current), [])

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigator.clipboard.writeText(text)
    if (!showCopiedText) {
      setShowCopiedText(true)
    }
    timeoutRef.current = setTimeout(() => {
      setShowCopiedText(false)
    }, hideLabelDelay)
  }

  return (
    <Wrapper
      type="button"
      className={className}
      title={t({ id: "label_copy_to_clipboard", message: "Copy to clipboard" })}
      onClick={handleClick}
      isCopied={showCopiedText}
    >
      <SvgIcon name={showCopiedText ? "clipboard-check" : "clipboard"}/>
      {showCopiedText && (
        <SuccessLabel>
          {t({ id: "label_copied", message: "Copied!" })}
        </SuccessLabel>
      )}
    </Wrapper>
  )
}

export default CopyToClipboard
